import { map, tap } from 'rxjs/operators';

import { Observable } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  GraphMutationBuilderPayload,
  HandleComplexRequest,
  Logger,
} from '@camelot/server';

import {
  FooterEntry,
  GridMutationResult,
  IDataService,
  IEditabledDataService,
  IEditabledDataUpdateFields,
  IFooterDataService,
} from '../models/types';
import {
  AddGridRowResponse,
  ConvertGridLineResponse,
  DeleteGridRowsResponse,
  DuplicateGridRowResponse,
  GridRow,
  MoveGridRowResponse,
  UpdateGridRowsResponse,
} from './dto/grid-row';
import { InsertPosition } from './dto/insert-position';
import {
  ADD_GRID_ROW,
  CONVERT_GRID_LINE,
  CamGridGraphConfig,
  DELETE_GRID_ROWS,
  DUPLICATE_GRID_ROW,
  GET_GRID_FOOTER,
  GET_GRID_ROWS,
  MOVE_GRID_ROW,
  UPDATE_GRID_ROWS,
} from './grid-graphql';

const POSITION_FIELDS: string[] = ['compositionPath', 'order', 'parentId'];

export abstract class CamBaseGridService<T extends GridRow> extends CamBaseService {
  public gridRows = new HandleComplexRequest<T[]>();

  protected abstract _graphEndpoint: GraphEndpoint;
  protected abstract _gridGraph: CamGridGraphConfig;

  fetchGridRows$(entityId: string, showHidden?: boolean) {
    return this.gridRows.fetch(
      entityId,
      this._logged$(
        'fetchGridRows',
        entityId,
        { showHidden },
        this._graphService.fetchQueryBuilder<T[]>(
          GET_GRID_ROWS(this._gridGraph, entityId, showHidden),
          this._graphEndpoint.clientName,
          'network-only'
        )
      )
    );
  }

  fetchGridFooter$(entityId: string) {
    return this._graphService.fetchQueryBuilder<FooterEntry[]>(
      GET_GRID_FOOTER(this._gridGraph, entityId),
      this._graphEndpoint.clientName,
      'network-only'
    );
  }

  updateGridRows$(entityId: string, fields: IEditabledDataUpdateFields<T>[]): Observable<GridMutationResult<T>> {
    return this._mutate$<UpdateGridRowsResponse<T>>(
      'updateGridRows',
      entityId,
      { fields },
      UPDATE_GRID_ROWS(this._gridGraph, entityId, fields),
      response => ({ update: response.affectedRows ?? [] })
    );
  }

  addGridRow$(
    entityId: string,
    rowType: number,
    relatedId?: string,
    position?: InsertPosition
  ): Observable<GridMutationResult<T>> {
    return this._mutate$<AddGridRowResponse<T>>(
      'addGridRow',
      entityId,
      { position, relatedId, rowType },
      ADD_GRID_ROW(this._gridGraph, entityId, rowType, relatedId, position),
      response => ({ add: [response.addedRow], update: response.affectedRows ?? [] })
    );
  }

  deleteGridRows$(entityId: string, id: string): Observable<GridMutationResult<T>> {
    return this._mutate$<DeleteGridRowsResponse<T>>(
      'deleteGridRows',
      entityId,
      { id },
      DELETE_GRID_ROWS(this._gridGraph, entityId, [id]),
      response => ({
        remove: (response.deletedIds ?? []).map(deletedId => ({ id: deletedId })),
        update: response.affectedRows ?? [],
      })
    );
  }

  convertGridLine$(entityId: string, id: string, newType: number): Observable<GridMutationResult<T>> {
    return this._mutate$<ConvertGridLineResponse<T>>(
      'convertGridLine',
      entityId,
      { id, newType },
      CONVERT_GRID_LINE(this._gridGraph, id, newType),
      // le back renvoie `convertedRow` sans sa position (ordre, chemin a vide) : on garde celle de la grille
      response => ({
        update: [
          {
            modifiedProperties: Object.keys(response.convertedRow).filter(key => !POSITION_FIELDS.includes(key)),
            row: response.convertedRow,
          },
          ...(response.affectedRows ?? []),
        ],
      })
    );
  }

  moveGridRow$(
    entityId: string,
    id: string,
    targetId: string,
    position: InsertPosition
  ): Observable<GridMutationResult<T>> {
    return this._mutate$<MoveGridRowResponse<T>>(
      'moveGridRow',
      entityId,
      { id, position, targetId },
      MOVE_GRID_ROW(this._gridGraph, id, targetId, position),
      // le back renvoie `movedRow` partiel (description, type a null) : seule sa position fait foi
      response => ({
        update: [
          { modifiedProperties: [...POSITION_FIELDS, 'index'], row: response.movedRow },
          ...(response.affectedRows ?? []),
        ],
      })
    );
  }

  duplicateGridRow$(
    entityId: string,
    sourceId: string,
    targetId: string,
    position: InsertPosition
  ): Observable<GridMutationResult<T>> {
    return this._mutate$<DuplicateGridRowResponse<T>>(
      'duplicateGridRow',
      entityId,
      { position, sourceId, targetId },
      DUPLICATE_GRID_ROW(this._gridGraph, sourceId, targetId, position),
      response => ({
        add: [...(response.addedRows ?? [])].sort(
          (a, b) => (a.compositionPath?.length ?? 0) - (b.compositionPath?.length ?? 0)
        ),
        update: response.affectedRows ?? [],
      })
    );
  }

  hideGridRow$(entityId: string, id: string): Observable<GridMutationResult<T>> {
    const hidden = !(this.gridRows.get(entityId) ?? []).find(row => row.id === id)?.hidden;
    return this.updateGridRows$(entityId, [{ hidden, id } as IEditabledDataUpdateFields<T>]);
  }

  editableDataService(entityId: string): IEditabledDataService<T> {
    return {
      addLine$: (type, relatedId, position) => this.addGridRow$(entityId, type, relatedId, position),
      convertLine$: (id, targetType) => this.convertGridLine$(entityId, id, targetType),
      deleteLine$: id => this.deleteGridRows$(entityId, id),
      duplicateLineTo$: (sourceId, targetId, position) =>
        this.duplicateGridRow$(entityId, sourceId, targetId, position),
      getData$: showHidden => this.fetchGridRows$(entityId, showHidden).pipe(map(rows => ({ items: rows }))),
      hideLine$: id => this.hideGridRow$(entityId, id),
      moveLine$: (id, targetId, position) => this.moveGridRow$(entityId, id, targetId, position),
      updateField$: fields => this.updateGridRows$(entityId, fields),
    };
  }

  footerDataService(entityId: string): IFooterDataService {
    return { getFooter$: () => this.fetchGridFooter$(entityId) };
  }

  /** Arbre en lecture seule, chaque niveau etant lu dans le cache plat `gridRows` (a charger via `fetchGridRows$`). */
  readOnlyDataService(entityId: string): IDataService<T> {
    return {
      getChildren$: params => {
        const parentId = params.parentNode.data?.id ?? null;
        return this.gridRows.get$(entityId).pipe(
          map(rows => {
            const items = rows.filter(row => (row.parentId ?? null) === parentId).sort((a, b) => a.index - b.index);
            return { items, totalCount: items.length };
          })
        );
      },
    };
  }

  private _mutate$<R>(
    operation: string,
    entityId: string,
    request: object,
    payload: GraphMutationBuilderPayload,
    toResult: (response: R) => GridMutationResult<T>
  ): Observable<GridMutationResult<T>> {
    return this._logged$(
      operation,
      entityId,
      request,
      this._graphService.mutateBuilder<R>(payload, this._graphEndpoint.clientName)
    ).pipe(
      map(toResult),
      tap(result => this._syncCache(entityId, result))
    );
  }

  // `gridRows` sert au toggle de `hidden` et a la grille en lecture : il doit suivre chaque mutation
  private _syncCache(entityId: string, result: GridMutationResult<T>) {
    const rows = this.gridRows.get(entityId);
    if (!rows) {
      return;
    }

    const removed = new Set((result.remove ?? []).map(({ id }) => id));
    const byId = new Map(rows.filter(row => !removed.has(row.id)).map(row => [row.id, row]));
    (result.update ?? []).forEach(({ modifiedProperties, row }) => {
      const existing = byId.get(row.id);
      const changes = modifiedProperties?.length
        ? Object.fromEntries(modifiedProperties.map(prop => [prop, (row as Record<string, unknown>)[prop]]))
        : row;
      byId.set(row.id, existing ? { ...existing, ...changes } : row);
    });
    (result.add ?? []).forEach(row => byId.set(row.id, row));
    // `add` fusionnerait l'ancien tableau en objet : on remplace l'entree telle quelle
    this.gridRows.update(entityId, [...byId.values()], false);
  }

  private _logged$<R>(operation: string, entityId: string, request: object, call$: Observable<R>): Observable<R> {
    const label = `[GRID-EDITABLE][CamBaseGridService] ${operation}`;
    Logger.LogDebug(`${label} → request`, { entityId, ...request });
    return call$.pipe(tap(response => Logger.LogDebug(`${label} ← response`, { entityId, response })));
  }
}
